import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import type { Tenant } from "@/mocks/superadmin/tenants";
import { adminApi, type AdminTenant } from "@/lib/superadmin";
import type { Tier } from "@/lib/access";
import TenantFilters from "./components/TenantFilters";
import TenantTable from "./components/TenantTable";
import ImpersonateModal from "./components/ImpersonateModal";
import {
  PageHeader,
  GhostButton,
  ErrorBanner,
  Card,
  Skeleton,
} from "../components/ui";

type SortKey = keyof Tenant;

/** Map a real API tenant onto the shape the UI components render. */
function toTenant(t: AdminTenant): Tenant {
  return {
    id: t.id,
    businessName: t.businessName,
    slug: t.slug,
    ownerName: t.ownerName,
    email: t.email,
    phone: t.phone ?? "—",
    city: t.city,
    address: t.address ?? "—",
    gstin: t.gstin ?? "—",
    fssai: t.fssai ?? "—",
    plan: t.plan,
    status: t.status,
    outlets: t.outlets,
    signupDate: t.signupDate,
    lastActive: t.lastActive ?? "—",
    monthlyRevenue: t.monthlyRevenue,
    ordersPerDay: t.ordersPerDay,
  };
}

export default function TenantsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Tenant[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [sortKey, setSortKey] = useState<SortKey>("lastActive");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [impersonateTenant, setImpersonateTenant] = useState<Tenant | null>(null);
  const [impersonating, setImpersonating] = useState(false);
  const [canImpersonate, setCanImpersonate] = useState(true);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await adminApi.tenants.list({
        search: search.trim() || undefined,
        status: statusFilter || undefined,
        plan: planFilter || undefined,
        city: cityFilter || undefined,
      });
      setRows(res.rows.map(toTenant));
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load tenants.");
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, planFilter, cityFilter]);

  // Debounced reload whenever filters change.
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  // Client-side sort over the loaded page.
  const sorted = useMemo(() => {
    const result = [...rows];
    result.sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }
      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();
      return sortDir === "asc" ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
    return result;
  }, [rows, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleStatusChange = async (tenant: Tenant, newStatus: Tenant["status"]) => {
    // The API only models active <-> suspended; trial/churned are derived
    // server-side and not directly settable here.
    if (newStatus !== "active" && newStatus !== "suspended") {
      showToast("Only Activate / Suspend are available.");
      return;
    }
    try {
      await adminApi.tenants.setStatus(tenant.id, newStatus);
      showToast(
        `${tenant.businessName} has been ${newStatus === "active" ? "activated" : "suspended"}.`
      );
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Action failed.");
    }
  };

  const handlePlanChange = async (tenant: Tenant, tier: Tier) => {
    try {
      await adminApi.tenants.setPlan(tenant.id, tier);
      showToast(`${tenant.businessName} moved to ${tier} plan.`);
      await load();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not change plan.");
    }
  };

  const handleImpersonate = (tenant: Tenant) => {
    setImpersonateTenant(tenant);
  };

  const openDetail = (tenant: Tenant) => {
    navigate(`/superadmin/tenants/${tenant.id}`, { state: { tenant } });
  };

  const handleConfirmImpersonate = async () => {
    if (!impersonateTenant) return;
    setImpersonating(true);
    try {
      const { url } = await adminApi.tenants.impersonateLink(impersonateTenant.id);
      window.open(url, "_blank", "noopener");
      showToast(`Opened ${impersonateTenant.businessName} session.`);
      setImpersonateTenant(null);
    } catch (e) {
      // If the backend hasn't shipped impersonation, hide the button next time.
      setCanImpersonate(false);
      showToast(e instanceof Error ? e.message : "Impersonation unavailable.");
      setImpersonateTenant(null);
    } finally {
      setImpersonating(false);
    }
  };

  const stats = useMemo(
    () => ({
      total,
      active: rows.filter((t) => t.status === "active").length,
      trial: rows.filter((t) => t.status === "trial").length,
      churned: rows.filter((t) => t.status === "churned").length,
      suspended: rows.filter((t) => t.status === "suspended").length,
    }),
    [rows, total]
  );

  const exportCsv = () => {
    const header = [
      "Business", "Slug", "Owner", "Email", "Phone", "City", "Plan",
      "Status", "Outlets", "Signup", "Last Active", "Monthly Revenue", "Orders/Day",
    ];
    const lines = sorted.map((t) =>
      [
        t.businessName, t.slug, t.ownerName, t.email, t.phone, t.city, t.plan,
        t.status, t.outlets, t.signupDate, t.lastActive, t.monthlyRevenue, t.ordersPerDay,
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[header.join(","), ...lines].join("\n")], {
      type: "text/csv;charset=utf-8",
    });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `tenants-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    showToast(`Exported ${sorted.length} tenants to CSV.`);
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      {toast && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2 bg-foreground-900 text-background-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-[slideIn_0.3s_ease-out]">
          <i className="ri-check-line text-emerald-400"></i>
          {toast}
        </div>
      )}

      <PageHeader
        title="Tenant Management"
        subtitle="Manage all restaurant accounts, their status, and configurations"
        actions={
          <>
            <GhostButton onClick={exportCsv} title="Export current view as CSV">
              <i className="ri-download-2-line"></i>
              Export CSV
            </GhostButton>
            <GhostButton onClick={() => void load()}>
              <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i>
              Refresh
            </GhostButton>
          </>
        }
      />

      <div className="grid grid-cols-5 gap-3">
        {[
          { label: "Total", value: stats.total, color: "text-foreground-900", dot: "bg-foreground-400" },
          { label: "Active", value: stats.active, color: "text-emerald-600", dot: "bg-emerald-500" },
          { label: "Trial", value: stats.trial, color: "text-amber-600", dot: "bg-amber-400" },
          { label: "Churned", value: stats.churned, color: "text-rose-500", dot: "bg-rose-400" },
          { label: "Suspended", value: stats.suspended, color: "text-slate-500", dot: "bg-slate-400" },
        ].map((s) => (
          <Card key={s.label} className="px-4 py-3 text-center hover:shadow-md transition-shadow" padded={false}>
            <p className={`text-xl font-heading font-bold ${s.color}`}>{s.value}</p>
            <p className="flex items-center justify-center gap-1.5 text-[11px] text-foreground-500 font-medium mt-0.5">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`}></span>
              {s.label}
            </p>
          </Card>
        ))}
      </div>

      <TenantFilters
        search={search}
        onSearchChange={setSearch}
        planFilter={planFilter}
        onPlanChange={setPlanFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        cityFilter={cityFilter}
        onCityChange={setCityFilter}
        totalResults={total}
        filteredCount={sorted.length}
      />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {loading ? (
        <Card className="space-y-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </Card>
      ) : (
        <TenantTable
          tenants={sorted}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSort}
          onViewDetails={openDetail}
          onImpersonate={canImpersonate ? handleImpersonate : undefined}
          onStatusChange={handleStatusChange}
          onPlanChange={handlePlanChange}
        />
      )}

      {impersonateTenant && (
        <ImpersonateModal
          tenant={impersonateTenant}
          onClose={() => setImpersonateTenant(null)}
          onConfirm={handleConfirmImpersonate}
          loading={impersonating}
        />
      )}
    </div>
  );
}
